import { FC, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Flex, Tabs, message, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import dayjs from "dayjs";
import useDataList from "@/hooks/useDataList";
import STableLayout, { TableLayoutRef, DEFAULT_PAGE_SIZE } from "@/styles/STableLayout";
import { stateList, ProductInfoStateListParams, enableProductInfo, disableProductInfo } from "@/apis/productInfo";
import { ProductInfoStateEnum, ProductInfoData } from "@/types/productInfo";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import AddOrEdit from "./components/AddOrEdit";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import OrderForm from "@/pages/Sales/components/OrderForm";
import { SaleItemState } from "@/types/sales";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface ProductInfoFilters {
  keyword: string;
}

const INITIAL_PRODUCT_INFO_FILTERS: ProductInfoFilters = {
  keyword: "",
};

const ProductInfo: FC = () => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<ProductInfoFilters>(INITIAL_PRODUCT_INFO_FILTERS);
  const { keyword } = draftFilters;
  const [activeTab, setActiveTab] = useState<ProductInfoStateEnum>(ProductInfoStateEnum.NORMAL);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [orderDetail, setOrderDetail] = useState<SaleItemState | null>(null);

  // 使用 useDataList hook 获取数据
  const [list, total, listLoading, { fetchList, rmItem, updateItem, addItem }] = useDataList<ProductInfoData, [ProductInfoStateListParams]>({
    fetchApi: stateList
  });

  // 获取数据的函数
  const fetch = (
    offset: number,
    filters: ProductInfoFilters,
    state: ProductInfoStateEnum,
  ) => {
    const params: ProductInfoStateListParams = {
      state,
      params: {
        body: filters.keyword,
        pagination: {
          offset,
          take: tableRef.current?.pageSize || DEFAULT_PAGE_SIZE
        }
      }
    };
    fetchList(params);
  };

  // 导出全部数据
  const handleExportAll = async (): Promise<ProductInfoData[]> => {
    setExportLoading(true);
    setExportProgress(0);
    const allDataList: ProductInfoData[] = [];
    
    try {
      const batchSize = 100; // 每批次获取500条
      
      // 第一次请求获取总数
      const firstRes = await stateList({
        state: activeTab,
        params: {
          body: appliedFilters.keyword,
          pagination: { offset: 0, take: batchSize }
        }
      });

      if (firstRes?.body) {
        allDataList.push(...firstRes.body);
      }

      // 根据返回的 pagination.size 计算总批次数
      const totalSize = firstRes?.pagination?.size ?? 0;
      const batches = Math.ceil(totalSize / batchSize);

      // 继续请求剩余数据
      for (let i = 1; i < batches; i++) {
        const offset = i * batchSize;
        const res = await stateList({
          state: activeTab,
          params: {
            body: appliedFilters.keyword,
            pagination: { offset, take: batchSize }
          }
        });
        if (res?.body) {
          allDataList.push(...res.body);
        }
        // 更新进度
        setExportProgress(Math.round(((i + 1) / batches) * 100));
      }
      return allDataList;
    } catch (error) {
      message.error(t('common.exportErr'));
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  // 导出列配置
  const exportColumns = (t: any) => [
    { key: 'product.salesOrderNumber', label: t('product.salesOrderNumber') },
    { key: 'product.internalNumber', label: t('product.internalNumber') },
    { key: 'product.customerName', label: t('product.customerName') },
    { key: 'product.customerModel', label: t('product.customerModel') },
    { key: 'product.companyModel', label: t('product.companyModel') },
    { key: 'product.softwareVersion', label: t('product.softwareVersion') },
    { key: 'product.productionSoftware', label: t('product.productionSoftware') },
    { key: 'product.quantity', label: t('product.quantity') },
    { key: 'product.size', label: t('product.size') },
    { key: 'product.issueDate', label: t('product.issueDate') },
    { key: 'product.flash', label: t('product.flash') },
    { key: 'product.md5Value', label: t('product.md5Value') },
    { key: 'product.checksum', label: t('product.checksum') },
    { key: 'product.qaToolVersion', label: t('product.qaToolVersion') },
    { key: 'product.macAddressContent', label: t('product.macAddressContent') },
    { key: 'product.antivirusStatus', label: t('product.antivirusStatus') },
    { key: 'product.languageRequirement', label: t('product.languageRequirement') },
    { key: 'product.ssid', label: t('product.ssid') },
    { key: 'product.initialIp', label: t('product.initialIp') },
    { key: 'product.finishedToolVersion', label: t('product.finishedToolVersion') },
    { key: 'product.semiFinishedToolVersion', label: t('product.semiFinishedToolVersion') },
    { key: 'product.channelRange', label: t('product.channelRange') },
    { key: 'product.channelSelection', label: t('product.channelSelection') },
    { key: 'product.defaultChannel', label: t('product.defaultChannel') },
    { key: 'product.functionDescription', label: t('product.functionDescription') },
    { key: 'product.productionTestProgram', label: t('product.productionTestProgram') },
    { key: 'product.power', label: t('product.power') },
    { key: 'product.evm', label: t('product.evm') },
    { key: 'product.fer', label: t('product.fer') },
    { key: 'product.smt', label: t('product.smt') },
    { key: 'product.dip', label: t('product.dip') },
    { key: 'product.test1', label: t('product.test1') },
    { key: 'product.assy', label: t('product.assy') },
    { key: 'product.test2', label: t('product.test2') },
    { key: 'product.pack', label: t('product.pack') },
    { key: 'product.shipmentRequirements', label: t('product.shipmentRequirements') },
    { key: 'product.createdAt', label: t('product.createdAt') },
    { key: 'product.updatedAt', label: t('product.updatedAt') },
  ];

  // 默认选中的列
  const defaultSelectedColumns = (t: any) => [
    'product.salesOrderNumber',
    'product.internalNumber',
    'product.customerName',
    'product.customerModel',
    'product.companyModel',
    'product.softwareVersion',
    'product.quantity',
  ];

  // 导出数据映射函数
  const exportDataMapper = (item: ProductInfoData, t: any) => ({
    [t('product.salesOrderNumber')]: item?.saleOrderItem?.order?.number || '',
    [t('product.internalNumber')]: item?.saleOrderItem?.order?.innerNumber || '',
    [t('product.customerName')]: item?.customerName || '',
    [t('product.customerModel')]: item?.customerModel || '',
    [t('product.companyModel')]: item?.companyModel || '',
    [t('product.softwareVersion')]: item?.softwareVersion || '',
    [t('product.productionSoftware')]: item?.productionSoftware || '',
    [t('product.quantity')]: item?.quantity || '',
    [t('product.size')]: item?.size || '',
    [t('product.issueDate')]: item?.issueDate ? dayjs(item.issueDate).format("YYYY-MM-DD") : '',
    [t('product.flash')]: item?.flash || '',
    [t('product.md5Value')]: item?.md5Value || '',
    [t('product.checksum')]: item?.checksum || '',
    [t('product.qaToolVersion')]: item?.qaToolVersion || '',
    [t('product.macAddressContent')]: item?.macAddressContent || '',
    [t('product.antivirusStatus')]: item?.antivirusStatus || '',
    [t('product.languageRequirement')]: item?.languageRequirement ? String(item.languageRequirement) : '',
    [t('product.ssid')]: item?.ssid || '',
    [t('product.initialIp')]: item?.initialIp || '',
    [t('product.finishedToolVersion')]: item?.finishedToolVersion || '',
    [t('product.semiFinishedToolVersion')]: item?.semiFinishedToolVersion || '',
    [t('product.channelRange')]: item?.channelRange ? String(item.channelRange) : '',
    [t('product.channelSelection')]: item?.channelSelection ? String(item.channelSelection) : '',
    [t('product.defaultChannel')]: item?.defaultChannel ? String(item.defaultChannel) : '',
    [t('product.functionDescription')]: item?.functionDescription || '',
    [t('product.productionTestProgram')]: item?.productionTestProgram || '',
    [t('product.power')]: item?.power || '',
    [t('product.evm')]: item?.evm || '',
    [t('product.fer')]: item?.fer || '',
    [t('product.smt')]: item?.smt || '',
    [t('product.dip')]: item?.dip || '',
    [t('product.test1')]: item?.test1 || '',
    [t('product.assy')]: item?.assy || '',
    [t('product.test2')]: item?.test2 || '',
    [t('product.pack')]: item?.pack || '',
    [t('product.shipmentRequirements')]: item?.shipmentRequirements || '',
    [t('product.createdAt')]: dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss"),
    [t('product.updatedAt')]: dayjs(item.updatedAt).format("YYYY-MM-DD HH:mm:ss"),
  });

  // 初始加载
  useEffect(() => {
    fetch(
      0,
      INITIAL_PRODUCT_INFO_FILTERS,
      ProductInfoStateEnum.NORMAL,
    );
    // 初始加载只执行一次，fetchList 的引用会随 useDataList 渲染变化。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters, activeTab);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_PRODUCT_INFO_FILTERS);
    fetch(0, nextFilters, activeTab);
    tableRef.current?.resetTablePage();
  };

  const handleTabChange = (tab: string) => {
    const nextTab = Number(tab) as ProductInfoStateEnum;
    setActiveTab(nextTab);
    fetch(0, appliedFilters, nextTab);
  };

  // Tab 定义
  const tableTabs = [
    {
      key: ProductInfoStateEnum.NORMAL + '',
      label: t("product.normalTab"),
      disabled: listLoading
    },
    {
      key: ProductInfoStateEnum.DISABLED + '',
      label: t("product.disabledTab"),
      disabled: listLoading
    },
  ];

  // 启用/禁用操作
  const { onConfirm: handleToggleState } = useFrontDelTip({
    cb: async (record: ProductInfoData) => {
      const isDisable = activeTab === ProductInfoStateEnum.NORMAL;
      const successKey = isDisable ? "disableSuccess" : "enableSuccess";
      const failedKey = isDisable ? "disableFailed" : "enableFailed";

      try {
        if (isDisable) {
          await disableProductInfo(record.id);
        } else {
          await enableProductInfo(record.id);
        }
        message.success(t(`product.${successKey}`));
        rmItem(record.id);
      } catch (error) {
        message.error(t(`product.${failedKey}`));
        throw error;
      }
    },
    message: t(activeTab === ProductInfoStateEnum.NORMAL ? "product.disableConfirm" : "product.enableConfirm"),
    desc: t(activeTab === ProductInfoStateEnum.NORMAL ? "product.disableConfirmDesc" : "product.enableConfirmDesc")
  });

  // 新增/编辑成功回调
  const handleAddOrEditSuccess = (res: ProductInfoData) => {
    if (editingId) {
      // 编辑模式：更新列表中的数据
      updateItem(res);
    } else {
      // 新增模式：添加到列表
      addItem(res);
    }
  };

  // 打开新增弹窗
  const handleOpenAdd = () => {
    setEditingId(undefined);
    setAddEditOpen(true);
  };

  // 关闭弹窗
  const handleCloseAddEdit = () => {
    setEditingId(undefined);
    setAddEditOpen(false);
  };

  // 表格列定义
  const columns: ColumnsType<ProductInfoData> = [
    {
      title: t("product.salesOrderNumber"),
      dataIndex: ["saleOrderItem", "order", "number"],
      key: "orderNumber",
      width: 220,
      ellipsis: true,
      sorter: (a, b) => {
        const valA = a?.saleOrderItem?.order?.number ?? "";
        const valB = b?.saleOrderItem?.order?.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => (
        <Typography.Text
          ellipsis={{ tooltip: record?.saleOrderItem?.order?.number ?? "" }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => record.saleOrderItem && setOrderDetail(record.saleOrderItem)}
        >
          {record?.saleOrderItem?.order?.number ?? ""}
        </Typography.Text>
      ),
    },
    {
      title: t("product.internalNumber"),
      dataIndex: ["saleOrderItem", "order", "innerNumber"],
      key: "internalNumber",
      width: 150,
      ellipsis: true,
      sorter: (a, b) => {
        const valA = a?.saleOrderItem?.order?.innerNumber ?? "";
        const valB = b?.saleOrderItem?.order?.innerNumber ?? "";
        return valA.localeCompare(valB);
      },
    },
    {
      title: t("product.customerName"),
      dataIndex: "customerName",
      key: "customerName",
      width: 120,
      ellipsis: true,
    },
    {
      title: t("product.customerModel"),
      dataIndex: "customerModel",
      key: "customerModel",
      width: 150,
      ellipsis: true,
    },
    {
      title: t("product.companyModel"),
      dataIndex: "companyModel",
      key: "companyModel",
      width: 150,
      ellipsis: true,
    },
    {
      title: t("product.softwareVersion"),
      dataIndex: "softwareVersion",
      key: "softwareVersion",
      width: 120,
    },
    {
      title: t("product.productionSoftware"),
      dataIndex: "productionSoftware",
      key: "productionSoftware",
      width: 150,
      ellipsis: true,
    },
    {
      title: t("product.quantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
    },
    {
      title: t("product.size"),
      dataIndex: "size",
      key: "size",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("product.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (time: number) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: t("product.updatedAt"),
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (time: number) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: t("product.action"),
      key: "action",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Flex gap={8}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingId(record.id);
              setAddEditOpen(true);
            }}
          >
            {t("common.detail")}
          </Button>
          <Button
            type="link"
            size="small"
            danger={activeTab === ProductInfoStateEnum.NORMAL}
            onClick={() => handleToggleState(record)}
          >
            {activeTab === ProductInfoStateEnum.NORMAL ? t("product.disable") : t("product.enable")}
          </Button>
        </Flex>
      ),
    },
  ];

  // 搜索框
  const headerChildren = (
    <Flex justify="space-between" align="baseline" style={{ width: "100%" }}>
      <Tabs
        onChange={handleTabChange}
        type="card"
        activeKey={activeTab + ''}
        items={tableTabs}
      />
      <Flex justify="flex-end" gap={10} align="center" style={{ flex: 1 }}>
        <Button type="primary" onClick={handleOpenAdd}>
          {t("product.createProductInfo")}
        </Button>
        <ExportExcelButton<ProductInfoData>
          list={list}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName={`product_info_${activeTab === ProductInfoStateEnum.NORMAL ? 'normal' : 'disabled'}.xlsx`}
          sheetName='Product Info'
        />
        <ExportExcelButton<ProductInfoData>
          list={list}
          isAll
          loadData={handleExportAll}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName={`product_info_all_${activeTab === ProductInfoStateEnum.NORMAL ? 'normal' : 'disabled'}.xlsx`}
          sheetName='Product Info'
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <ListFilterBar
            onSearch={handleSearch}
            onReset={handleClearFilters}
            disabled={listLoading}
          >
            {({ submit }) => (
              <Input
                placeholder={t("product.searchPlaceholder")}
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value })
                }
                onPressEnter={submit}
                style={{ width: 300 }}
              />
            )}
          </ListFilterBar>
        </div>
      </Flex>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout style={{ flex: 1 }}
        headerChildren={headerChildren}
        inputWrapStyle={{ padding: 0, marginBottom: -10 }}
        tableLayoutRef={tableRef}
        columns={columns as ColumnsType<AnyObject>}
        dataSource={list}
        loading={listLoading}
        total={total}
        rowKey="id"
        handleChange={offset => fetch(offset, appliedFilters, activeTab)}
        scrollX={1100}
      />
      {addEditOpen && <AddOrEdit
        open={addEditOpen}
        id={editingId}
        isDisable={activeTab === ProductInfoStateEnum.DISABLED}
        onClose={handleCloseAddEdit}
        onSuccess={handleAddOrEditSuccess}
      />}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {orderDetail && <OrderForm
        key={orderDetail?.id}
        data={orderDetail}
        open={!!orderDetail}
        handleCancel={() => setOrderDetail(null)}
        leftTitle={t("common.detail")}
        disabledItems={[
          'innerNumber',
          'receiver',
          'receiverContact',
          'receiverMobile',
          'receiverAddress',
          'country',
          'extPrice',
          'taxRate',
          'shippingMethod',
          'itemPriceHis',
          'quantity',
          'bquantity',
          'discountedPrice',
          'discount',
          'bdiscountedPrice',
          'bdiscount',
          'mfgr',
          'customerModel',
          'reqDeliDate'
        ]}
        confirmLoading={false}
        handleOk={() => Promise.resolve()}
      />}
    </Flex>
  );
};

export default ProductInfo;
